import React, { createContext, memo, useContext, useMemo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { ChildrenPropType } from '../../../../prop-types/children';
import { TagPropType } from '../../../../prop-types/tag';
import { emptyArray } from 'ui-components/default-props/empty';
import { manageTags } from '../../../../store/tags/tags.actions';
import { useGetFulfillmentPlanIdsFromSalesOrderIdWithEligibleStatus } from '../../../../hooks/entities/selectors/fulfillment-plans';
import { EligibleToEditStatuses } from '../../../../enums/fulfillmentplan-order-grid-status';
import { bulkUpdateFulfillmentPlanTags } from '../../../../store/tags/tags.api';
import { useUpdateEntitiesFulfillmentPlans } from '../../hooks/use-update-entities-fulfillment-plans';

import messages from './messages';

const FulfillmentPlanTagsContext = createContext(undefined);

export const useFulfillmentPlanTagsContext = () => useContext(FulfillmentPlanTagsContext);
export const SAVE_TAGS_BUTTON_ID = 'fulfillment-plan-order-details-save-tags';

const FulfillmentPlanTagsContextProvider = ({
  fulfillmentPlanId,
  fulfillmentPlanTags = emptyArray,
  sellerTags = emptyArray,
  salesOrderId,
  children,
}) => {
  const dispatch = useDispatch();
  const [isBulkUpdate, setIsBulkUpdate] = useState(false);
  const [addTagIds, setAddTagIds] = useState(() => fulfillmentPlanTags.map((tag) => tag.tagId));
  const [removeTagIds, setRemoveTagIds] = useState([]);

  const updateEntities = useUpdateEntitiesFulfillmentPlans();

  const manageTagsAction = useCallback(() => dispatch(manageTags()), [dispatch]);
  const bulkFulfillmentPlanIds = useGetFulfillmentPlanIdsFromSalesOrderIdWithEligibleStatus(salesOrderId, EligibleToEditStatuses);

  const onAddFulfillmentPlanTags = useCallback((tagId) => {
    setAddTagIds((prev) => [...prev, tagId]);
    setRemoveTagIds((prev) => prev.filter((id) => id !== tagId));
  }, []);

  const onRemoveFulfillmentPlanTags = useCallback((tagId) => {
    setRemoveTagIds((prev) => [...prev, tagId]);
    setAddTagIds((prev) => prev.filter((id) => id !== tagId));
  }, []);

  const onTagToggled = useCallback(
    (tagId) => {
      const hasTag = addTagIds.indexOf(tagId) !== -1;
      return hasTag ? onRemoveFulfillmentPlanTags(tagId) : onAddFulfillmentPlanTags(tagId);
    },
    [addTagIds, onAddFulfillmentPlanTags, onRemoveFulfillmentPlanTags],
  );

  const toggleBulkUpdate = useCallback(() => {
    setIsBulkUpdate((prev) => !prev);
  }, [setIsBulkUpdate]);

  const clearTags = useCallback(() => {
    const currentTagIds = fulfillmentPlanTags.map((tag) => tag.tagId);
    setAddTagIds([]);
    setRemoveTagIds(currentTagIds);
  }, [fulfillmentPlanTags]);

  const saveTags = useCallback(async () => {
    const fulfillmentPlanIds = isBulkUpdate ? bulkFulfillmentPlanIds : [fulfillmentPlanId];

    const payload = {
      fulfillmentPlanIds,
      tagIdsToAdd: addTagIds,
      tagIdsToRemove: removeTagIds,
    };
    try {
      const { data } = await bulkUpdateFulfillmentPlanTags(payload);
      setIsBulkUpdate(false);
      updateEntities(data);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.bulkUpdateTagsSuccessTitle} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
        content: '',
      });
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.bulkUpdateTagsErrorTitle} />,
        content: <FormattedMessage {...messages.bulkUpdateTagsErrorMessage} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [addTagIds, bulkFulfillmentPlanIds, fulfillmentPlanId, isBulkUpdate, removeTagIds, updateEntities]);

  const value = useMemo(
    () => ({
      fulfillmentPlanId,
      fulfillmentPlanTags,
      sellerTags,
      isEmptyTagList: sellerTags.length === 0,
      manageTagsAction,
      onTagToggled,
      toggleBulkUpdate,
      isBulkUpdate,
      clearTags,
      saveTags,
      addTagIds,
    }),
    [
      fulfillmentPlanId,
      fulfillmentPlanTags,
      sellerTags,
      manageTagsAction,
      onTagToggled,
      toggleBulkUpdate,
      isBulkUpdate,
      clearTags,
      saveTags,
      addTagIds,
    ],
  );

  return <FulfillmentPlanTagsContext.Provider value={value}>{children}</FulfillmentPlanTagsContext.Provider>;
};

export default memo(FulfillmentPlanTagsContextProvider);

FulfillmentPlanTagsContextProvider.propTypes = {
  salesOrderId: PropTypes.string.isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
  children: ChildrenPropType.isRequired,
  fulfillmentPlanTags: PropTypes.arrayOf(TagPropType),
  sellerTags: PropTypes.arrayOf(TagPropType),
};
